import { useEffect, useState } from 'react';
import { X, Tag } from 'lucide-react';
import { useStorefront } from '@/hooks/useStorefront';
import { useLanguage } from '@/hooks/useLanguage';

/**
 * Aktions-Topbar für befristete Rabattcodes.
 * - Nur auf der CH-Storefront sichtbar.
 * - Verschwindet automatisch nach Ablauf (Europe/Zurich, +02:00 im Sommer).
 * - Schliessbar; Auswahl bleibt bis Ablauf der Aktion gespeichert.
 */
const PROMO = {
  id: '1august-2026',
  code: '1AUGUST',
  endsAt: new Date('2026-08-01T23:59:59+02:00').getTime(),
  text: '20% Rabatt auf alles mit Code',
};

const COPY: Record<string, { text: string; ends: string; copied: string; copy: string; close: string }> = {
  de: { text: '20% Rabatt auf alles mit Code', ends: 'endet in', copied: 'Kopiert!', copy: 'Code kopieren', close: 'Hinweis schliessen' },
  en: { text: '20% off everything with code', ends: 'ends in', copied: 'Copied!', copy: 'Copy code', close: 'Close notice' },
  it: { text: '20% di sconto su tutto con il codice', ends: 'termina tra', copied: 'Copiato!', copy: 'Copia codice', close: 'Chiudi avviso' },
  fr: { text: '20% de réduction sur tout avec le code', ends: 'se termine dans', copied: 'Copié !', copy: 'Copier le code', close: "Fermer l'avis" },
};

const DISMISS_KEY = `promo_dismissed_${PROMO.id}`;

function pad(n: number) {
  return String(n).padStart(2, '0');
}

export function usePromoBarVisible(): boolean {
  const { storefront } = useStorefront();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (storefront !== 'ch') return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === '1';
    } catch {}
    if (dismissed) return;
    const check = () => setVisible(Date.now() < PROMO.endsAt);
    check();
    const t = window.setInterval(check, 1000);
    const onDismiss = () => setVisible(false);
    window.addEventListener('bf:promo-dismiss', onDismiss);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('bf:promo-dismiss', onDismiss);
    };
  }, [storefront]);

  return visible;
}

export default function PromoBar() {
  const visible = usePromoBarVisible();
  const { locale } = useLanguage();
  const c = COPY[locale] ?? COPY.de;
  const [remaining, setRemaining] = useState(() => PROMO.endsAt - Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const t = window.setInterval(() => setRemaining(PROMO.endsAt - Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  if (!visible || remaining <= 0) return null;

  const totalSec = Math.floor(remaining / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {}
    window.dispatchEvent(new Event('bf:promo-dismiss'));
  };

  const copy = () => {
    navigator.clipboard?.writeText(PROMO.code).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      },
      () => {},
    );
  };

  return (
    <div className="relative z-[1300] flex h-10 items-center justify-center gap-2 bg-[hsl(0_62%_47%)] px-10 text-center text-xs font-medium text-white sm:text-sm">
      <Tag className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden />
      <span className="truncate">
        {c.text}{' '}
        <button
          onClick={copy}
          className="rounded bg-white/20 px-1.5 py-0.5 font-mono font-bold tracking-wide transition-colors hover:bg-white/30"
          title={c.copy}
        >
          {copied ? c.copied : PROMO.code}
        </button>
      </span>
      <span className="hidden font-mono tabular-nums opacity-90 sm:inline">
        {c.ends} {pad(h)}:{pad(m)}:{pad(s)}
      </span>
      <span className="font-mono tabular-nums opacity-90 sm:hidden">
        {pad(h)}:{pad(m)}:{pad(s)}
      </span>
      <button
        onClick={dismiss}
        aria-label={c.close}
        className="absolute right-2 rounded p-1 transition-colors hover:bg-white/20"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}